import { useState } from 'react';
import { Button, Card, useToast } from '@/components/ui';
import {
  useCancellablePrescriptions,
  useCancelPrescription,
  type CancellablePrescription,
} from '@/features/orders/api';
import { isApiError } from '@/lib/api';
import { CLINIC_TZ } from '@/lib/clinic-time';
import { CancelPrescriptionDialog } from '@/pages/clinical/CancelPrescriptionDialog';
import styles from './patients.module.css';

/**
 * RL-1837: the clinic manager (prescriptions:cancel) cancels another
 * doctor's signed prescription that is not yet dispensed (e.g. a locum who
 * has left, a wrong prescription spotted later).
 */
export function CancellablePrescriptions({ patientId }: { patientId: string }) {
  const { data, isError } = useCancellablePrescriptions(patientId, true);
  const cancel = useCancelPrescription();
  const toast = useToast();
  const [open, setOpen] = useState<CancellablePrescription | null>(null);
  return (
    <Card title="Prescriptions not yet dispensed">
      {isError ? (
        <p className={styles.note}>Could not load the prescriptions.</p>
      ) : !data?.length ? (
        <p className={styles.note}>None waiting at the dispensary.</p>
      ) : (
        <ul className={styles.list}>
          {data.map((rx) => (
            <li key={rx.id}>
              <span>
                <strong>{rx.number ?? 'Prescription'}</strong> ·{' '}
                {rx.prescriber.fullName}
                {rx.signedAt
                  ? ` · ${new Date(rx.signedAt).toLocaleDateString('en-GB', {
                      timeZone: CLINIC_TZ,
                    })}`
                  : ''}{' '}
                · {rx.branch.name}
                <br />
                {rx.medicines.join(', ')}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setOpen(rx)}>
                Cancel
              </Button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <CancelPrescriptionDialog
          number={open.number}
          pending={cancel.isPending}
          onClose={() => setOpen(null)}
          onCancel={(reason) =>
            void cancel
              .mutateAsync({ id: open.id, reason })
              .then(() => {
                toast.show({
                  tone: 'success',
                  title: 'Prescription cancelled',
                });
                setOpen(null);
              })
              .catch((err: unknown) =>
                toast.show({
                  tone: 'error',
                  title: isApiError(err)
                    ? (err.problem?.title ?? 'Not cancelled')
                    : 'Not cancelled',
                  body: isApiError(err) ? err.message : undefined,
                  error: err,
                }),
              )
          }
        />
      )}
    </Card>
  );
}
